import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSETS_USER_DETAILS_METADATA, ASSETS_USER_DETAILS_TYPE, AssetsUser, AssetsUserDetails, FIREARM_METADATA, FIREARM_POOL_TYPE, FIREARM_TYPE, Firearm, FirearmPool, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { AssetsUserDetailsService, FirearmPoolService, FirearmService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class FirearmPoolItemComponentBase extends CrudItemComponent<FirearmPool, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  firearmSelectionList: Firearm[];

  managerSelectionList: AssetsUserDetails[];

  statusSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected firearmPoolService: FirearmPoolService,
    protected firearmService: FirearmService,
    protected managerService: AssetsUserDetailsService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(FIREARM_POOL_TYPE, fb, elementRef, router, route, userStorage, dialog, firearmPoolService, injector, snackbar);
  }

  createNewItem() {
    return new FirearmPool();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      firearmAssetId: [null, []],
      firearm: [null, [requiredValidator,]],
      manager: [null, [requiredValidator,]],
      businessUnit: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      lockVersion: [null, []],
      status: [null, [requiredValidator,]],
      updatedBy: [null, []],
      updatedDate: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.firearmAssetId?.disable();
    }
    this.businessUnit?.disable();
  }
  
  
  firearmDisplay(item: Firearm) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_TYPE.description(item);
  }
  
  firearmLoadSelectionList() {
    this.firearmService.findAll().subscribe(
      data => {
        this.firearmSelectionList = FIREARM_TYPE.entityDecorator.ensureContained(this.item && this.item.firearm, data.items);
      }
    );
  }

  firearmChanging(value: Firearm, unsetDependents: boolean = true) {}
  
  firearmChanged(value: Firearm, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.firearmChangedByUser(value, unsetDependents);
    }
  }  
  
  firearmChangedByUser(value: Firearm, unsetDependents: boolean = true) {
  }
  
  firearmCompare(a: any, b: any) {
    return FIREARM_METADATA.equals(a, b);
  }
  
  managerDisplay(item: AssetsUserDetails) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSETS_USER_DETAILS_TYPE.description(item);
  }
  
  managerLoadSelectionList() {
    this.managerService.findAll().subscribe(
      data => {
        this.managerSelectionList = ASSETS_USER_DETAILS_TYPE.entityDecorator.ensureContained(this.item && this.item.manager, data.items);
      }
    );
  }

  managerChanging(value: AssetsUserDetails, unsetDependents: boolean = true) {}
  
  managerChanged(value: AssetsUserDetails, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.managerChangedByUser(value, unsetDependents);
    }
  }  
  
  managerChangedByUser(value: AssetsUserDetails, unsetDependents: boolean = true) {
  }
  
  managerCompare(a: any, b: any) {
    return ASSETS_USER_DETAILS_METADATA.equals(a, b);
  }
  
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  statusLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_POOL_STATUS').subscribe(
      data => {
        this.statusSelectionList = data;
      }
    );
  }

  statusChanging(value: string, unsetDependents: boolean = true) {}
  
  statusChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.statusChangedByUser(value, unsetDependents);
    }
  }  
  
  statusChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get firearmAssetId() {
    return this.control('firearmAssetId');
  }
  
  get firearm() {
    return this.control('firearm');
  }
  
  get manager() {
    return this.control('manager');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get status() {
    return this.control('status');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
}





